// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {useContext, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import {useHistory} from 'react-router-dom';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import {Button} from '../../lib/button';
import {Editor} from '../../../../shared/editor/editor';
import Icon_chevron_left from '../../img/icons/24/chevron_left.svg';
import styles from './article_widget.module.less';
import {Index_scroll} from './index_scroll';
import {More_articles} from './more_articles';
import {Share} from './share';
import {Category_badge} from './category_badge';
import {Dynamic_heading} from '../../../../shared/editor/title_header_select';

interface i_article_props {
    category?: string | string[];
    title?: string;
    title_tag?: string;
    img?: string;
    author?: string;
    min_read?: number;
    content?: string;
    description?: string;
    related_title?: string;
}

interface i_article_editor_props {
    value: i_article_props;
    on_change: (v: i_article_props)=>void;
    on_cancel: ()=>void;
}

interface i_header_item {
    id: string;
    text: string;
    level: number;
}

export const Article_widget_editor = (props: i_article_editor_props)=>{
    const [state, set_state] = useState<i_article_props>(props.value || {});
    useEffect(()=>{
        if (props.value)
        {
            const formatted_value = {...props.value};
            if (Array.isArray(formatted_value.category))
                formatted_value.category = formatted_value.category.join(', ');
            set_state(formatted_value);
        }
    }, [props.value]);
    const handle_save = ()=>{
        const save_value = {...state};
        if (typeof save_value.category == 'string')
        {
            save_value.category = save_value.category.split(',')
                .map(cat=>cat.trim())
                .filter(cat=>cat);
        }
        props.on_change(save_value);
    };
    return <Editor
      value={state}
      editors={[
        {key: 'category', label: 'Categories (comma-separated)', type: 'text'},
        {key: 'title', label: 'Title', type: 'text'},
        {key: 'title_tag', label: 'Title Tag', type: 'select_tag_selector'},
        {key: 'description', label: 'Short description', type: 'textarea'},
        {key: 'img', label: 'Image URL', type: 'text'},
        {key: 'author', label: 'Author name', type: 'text'},
        {key: 'min_read', label: 'Minutes to read', type: 'number'},
        {key: 'content', label: 'Article content (HTML)', type: 'textarea'},
        {key: 'related_title', label: 'Related Articles Title', type: 'text'},
      ]}
      on_change={v=>set_state(v)}
      on_save={handle_save}
      on_cancel={props.on_cancel}
    />;
};

export const Article_widget = (props: i_article_props)=>{
    const {theme} = useContext(Settings_context);
    const history = useHistory();
    const article_ref = useRef<HTMLDivElement>(null);
    const [headers, set_headers] = useState<i_header_item[]>([]);
    const [active_header, set_active_header] = useState<string>('');
    useEffect(()=>{
        if (typeof window == 'undefined' || !article_ref.current)
            return;
        const article_body = article_ref.current;
        const header_elements = article_body.querySelectorAll('h2, h3, h4');
        if (!header_elements.length)
            return;
        const extracted_headers = [];
        header_elements.forEach((el, index)=>{
            const id = `header-${index}`;
            el.id = id;
            extracted_headers.push({
                id,
                text: el.textContent || '',
                level: parseInt(el.tagName.substring(1)),
            });
        });
        set_headers(extracted_headers);
        if ('IntersectionObserver' in window)
        {
            const observer = new IntersectionObserver(
                entries=>{
                    entries.forEach(entry=>{
                        if (entry.isIntersecting)
                            set_active_header(entry.target.id);
                    });
                },
                {
                    rootMargin: '-10% 0px -70% 0px',
                    threshold: [0, 0.25, 0.5, 0.75, 1]
                }
            );
            header_elements.forEach(el=>observer.observe(el));
            return ()=>observer.disconnect();
        }
    }, [props.content]);
    const scroll_to_header = id=>{
        const element = document.getElementById(id);
        if (element)
        {
            window.scrollTo({
                top: element.offsetTop - 80,
                behavior: 'smooth'
            });
        }
    };
    return <div className={cn(theme.container, styles.article_container)}>
      <div className={styles.back_button_container}>
        <Button
          caption='Back to blog'
          size='s'
          color='transparent'
          icon_left={<Icon_chevron_left />}
          on_click={()=>history.push('/blog')}
        />
      </div>
      <div className={styles.content_container}>
        <div className={styles.article_content}>
          {props.category &&
            <div className={styles.categories_container}>
              {(Array.isArray(props.category) ? props.category :
                [props.category]).map((cat, index)=>
                  <Category_badge key={index} category={cat}/>
              )}
          </div>}
          {props.title && <Dynamic_heading className={cn(styles.article_title)}
            tag={props.title_tag || 'h3'}>{props.title}</Dynamic_heading>}
          {props.img && <div className={styles.article_image}>
            <img src={props.img} alt={props.title || 'Article'}/>
          </div>}
          <div className={styles.article_meta}>
            <div className={styles.author_info}>
              <div className={styles.author_logo}>
                <div className={styles.logo_icon}></div>
              </div>
              <p className={cn(theme.p14, theme.med, styles.author_name)}>
                {props.author || 'Oculus'}
              </p>
              <span className={styles.dot}></span>
              <p className={cn(theme.p12, theme.med)}>
                {props.min_read || 5} min read
              </p>
            </div>
            <Share/>
          </div>
          <div
            className={styles.article_body}
            ref={article_ref}
            dangerouslySetInnerHTML={{__html: props.content || ''}}
          />
        </div>
        {headers.length > 0 &&
          <div className={styles.scrollable_index_container}>
          <Index_scroll
            headers={headers}
            active_header_id={active_header}
            on_header_click={scroll_to_header}
          />
        </div>}
      </div>
      <More_articles exclude_url={window.location.pathname} amount={3}
        title={props.related_title}/>
    </div>;
};
